import { useNavigate } from '@tanstack/react-router';
import { Chip } from '@/components/ui/Chip';
import { resolveDeepLinkTarget, type DeepLinkParts } from '@/lib/deep-links';
import { trackDeepLinkClick } from '@/lib/usage';
import type { ModelSchema } from '@arcanum-hq/shared-types';

interface DeepLinkProps {
  parts: DeepLinkParts;
  /**
   * Active model schema (refined at the route boundary). `null` defensively
   * yields an `invalid` chip — the agent emitted a deep link before the
   * schema mounted.
   */
  modelSchema: ModelSchema | null;
}

/**
 * Inline deep-link chip (directive §8.3 cousin, Phase 2 Session 5). Composes
 * the `<Chip>` primitive (Session 2) with TanStack Router navigate-on-click.
 *
 * I6 invariant: pre-validation against the loaded `ModelSchema`. Valid
 * targets render `<Chip variant="agent" interactive>`; invalid (unknown
 * cell / namedRange / surface, or `null` schema) render
 * `<Chip variant="invalid">` — click is a no-op, `aria-disabled="true"`,
 * `cursor: not-allowed`.
 *
 * The 2-link-per-response cap lives in the agent's system prompt §
 * `<deep_link_grammar>` (Phase 1 — server-side rule). Web does NOT
 * defensively enforce; if the agent emits 3+, all 3 render.
 */
export function DeepLink({ parts, modelSchema }: DeepLinkProps) {
  const navigate = useNavigate();
  const resolution = resolveDeepLinkTarget(parts, modelSchema);

  // Compose the visible label from the most informative param. Falls through
  // to the surface name when the link is just a route hop with no params.
  const label =
    parts.params.cell
      ?? parts.params.namedRange
      ?? parts.surface;

  if (!resolution.ok) {
    return (
      <Chip
        variant="invalid"
        label="goto"
        value={label}
      />
    );
  }

  const handleClick = () => {
    // Phase 4 Session 4 (2026-05-07) — fire-and-forget click telemetry. The
    // helper sends `surface` + `params_keys` (NEVER values — PII contract);
    // failures are swallowed so navigation is never blocked. Logged to
    // LangSmith on the API side via `POST /api/usage/deep-link-click`.
    trackDeepLinkClick({
      surface: parts.surface,
      params_keys: Object.keys(parts.params),
    });

    // TanStack Router's `navigate({ to, search })` is the canonical hop.
    // Search params survive the route remount; the target route reads them
    // (e.g. `?tab=model&cell=...` lands on the Builder Model tab; the
    // Phase 6 Session 3 rename added `?tab=signal` for the new Signal
    // surface; `?tab=workbench` is the renamed chat tab).
    //
    // The `unknown` cast is needed because TanStack Router's strict
    // generic NavigateOptions can't infer through our deep-link resolver
    // shape — the router validates search against the target route's
    // `validateSearch` schema at runtime, so a structural Record<string,
    // unknown> is the safest carrier.
    navigate({
      to: resolution.target.route,
      search: resolution.target.search,
    } as unknown as Parameters<typeof navigate>[0]);
  };

  return (
    <Chip
      variant="agent"
      label="goto"
      value={label}
      interactive
      onClick={handleClick}
    />
  );
}
